import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  RemoveEditOverlayType,
  AddNewModuleType,
  AddNewLayoutType,
  ChangeLayoutType,
  EditModuleType,
  DefaultThemeType,
  SendUpdatedAppConfig,
  SiteEngineConfigEvents,
  AddNewLayoutModuleType,
  ChangePageInfoType,
  EditLayoutModuleType,
  SendAppConfig,
  SaveWhiteLabelConfig,
  CreateDupInstanceType,
  RevertModuleConfigType,
  SetDynamicPageContent,
  SendNewDup,
  RestoreParent,
} from './interfaces';
import { updateConfig } from './store/config';
import { RootState } from './store/store';
import {
  pagesWithSamePath,
  insertAt,
  replaceStringElementInArr,
  handleUpdateLayoutModules,
  handleUpdatePageModules,
} from '@skygroup/shared/utils/siteEngineEditModuleHelpers';
import { setPageConfig } from './store/dynamicPageConfigSlice';
import { ensureModulesObjects } from '@skygroup/shared/utils/modulesConversion';

// interface Props {}

const useHandleMessagesFromBO = () => {
  const dispatch = useDispatch();
  const { config } = useSelector((data: RootState) => data.config);

  const [selectedInstance, setSelectedInstance] = useState<string | null>(null);

  useEffect(() => {
    const isEventOriginValid = (origin: string) =>
      origin === process.env.REACT_APP_BO_BASE_URL;

    const handleRemoveEditOverlay = (evData: RemoveEditOverlayType) => {
      setSelectedInstance(null);
    };

const handleAddNewModule = (evData: AddNewModuleType) => {
  evData?.instance && setSelectedInstance(evData?.instance);
  const moduleName = evData.moduleName; // instance name like 'Banner_abc123'

  const updatedPages = config.pages.map((page) => {
    if (
      page.allow_edit !== false &&
      (page.id === evData.pageId || pagesWithSamePath(page.path, evData.path))
    ) {
      // normalize existing modules to objects (this preserves numeric/fractional sort_index)
      const objs = ensureModulesObjects(page?.modules || []);

      // sort by actual numeric sort_index (ascending)
      const sorted = [...objs].sort((a: any, b: any) => {
        const na = Number(a?.sort_index);
        const nb = Number(b?.sort_index);
        if (!Number.isFinite(na) && !Number.isFinite(nb)) return 0;
        if (!Number.isFinite(na)) return 1;
        if (!Number.isFinite(nb)) return -1;
        return na - nb;
      });

      // target position (may be fractional)
      const targetPos = Number(evData.index);
      const safeNumber = (v: any) => {
        const n = Number(v);
        return Number.isFinite(n) ? n : null;
      };

      // find prev: max sort_index < targetPos
      let prevModule = null;
      let prevSort: number | null = null;
      for (let i = 0; i < sorted.length; i++) {
        const s = safeNumber(sorted[i]?.sort_index);
        if (s !== null && s < targetPos) {
          if (prevSort === null || s > prevSort) {
            prevSort = s;
            prevModule = sorted[i];
          }
        }
      }

      // find next: min sort_index > targetPos
      let nextModule = null;
      let nextSort: number | null = null;
      for (let i = 0; i < sorted.length; i++) {
        const s = safeNumber(sorted[i]?.sort_index);
        if (s !== null && s > targetPos) {
          if (nextSort === null || s < nextSort) {
            nextSort = s;
            nextModule = sorted[i];
          }
        }
      }

      // compute new module sort_index based on neighbors
      let computedSort: number;
      if (prevSort === null && nextSort === null) {
        // empty or all invalid -> default to 1
        computedSort = 1;
      } else if (prevSort === null) {
        // insert at head (before next)
        computedSort = nextSort !== null ? nextSort / 2 : 1;
      } else if (nextSort === null) {
        // insert at tail (after prev)
        computedSort = prevSort + 1;
      } else {
        // between two neighbors: average
        computedSort = (prevSort + nextSort) / 2;
      }

      // build the new module object
      const newModuleObj = { module: moduleName, sort_index: computedSort };

      // determine insertion index in the sorted array:
      // - if nextModule exists, insert before the first occurrence of nextSort
      // - otherwise append to the end
      let insertAtIndex = sorted.length; // default append
      if (nextModule && nextSort !== null) {
        insertAtIndex = sorted.findIndex((m) => {
          const s = safeNumber(m?.sort_index);
          return s !== null && s === nextSort;
        });
        if (insertAtIndex === -1) insertAtIndex = sorted.length;
      }

      // create new sorted array with new module inserted (do NOT reassign other sort_index values)
      const newSorted = [
        ...sorted.slice(0, insertAtIndex),
        newModuleObj,
        ...sorted.slice(insertAtIndex),
      ];

      // Return the page with modules replaced by the sorted list including the new module.
      // This maintains modules array ordered by sort_index and preserves existing sort_index values.
      return { ...page, modules: newSorted, id: evData.pageId };
    }
    return page;
  });

  const updatedConfig = {
    ...config,
    modulesConfigs: {
      ...config.modulesConfigs,
      [moduleName]: {
        ...evData?.defaultModuleObj,
        instance: moduleName,
      },
    },
    pages: updatedPages,
  };

  handleSendAllConfigs({ type: 'SendAppConfig', updatedConfig });

  return dispatch(updateConfig(updatedConfig));
};



    const handleAddNewLayoutModule = (evData: AddNewLayoutModuleType) => {
      const currentPage = config.pages.find(
        (page) => page.name === 'Package Info'
      );
      const currentLayout = currentPage?.layout ?? '';

      const cloneLayouts = { ...config.layouts };
      const updatedModules = insertAt(
        cloneLayouts[currentLayout]?.[evData.layoutId],
        evData.index,
        evData.module
      );

      const currentLayout2 = {
        ...cloneLayouts[currentLayout],
        [evData.layoutId]: updatedModules,
      };
      const layouts = { ...config.layouts, [currentLayout]: currentLayout2 };
      const updatedConfig = { ...config, layouts };

      // window.parent.postMessage(
      //   {
      //     type: 'UpdateWhiteLabelConfigs',
      //     messageArr: [
      //       { key: 'layouts', value: { [currentLayout]: currentLayout2 } },
      //     ],
      //   },
      //   '*'
      // );
      dispatch(updateConfig(updatedConfig));
    };

    const handleChangeLayout = (evData: ChangeLayoutType) => {
      const updatedPages = config.pages.map((page) => {
        if (
          page.id === evData.pageId ||
          pagesWithSamePath(page.path, evData.path)
        ) {
          return {
            ...page,
            layout: evData.updatedLayout,
            id: evData.pageId,
          };
        }
        return page;
      });

      const updatedConfig = { ...config, pages: updatedPages };

      handleSendAllConfigs({ type: 'SendAppConfig', updatedConfig });

      // window.parent.postMessage(
      //   {
      //     type: 'UpdateWhiteLabelConfigs',
      //     messageArr: [{ key: 'pages', value: updatedPages }],
      //   },
      //   '*'
      // );

      dispatch(updateConfig(updatedConfig));
    };

    const handleChangePageInfo = (evData: ChangePageInfoType) => {
      const updatedPages = config.pages.map((page) => {
        if (
          page.id === evData.pageId ||
          pagesWithSamePath(page.path, evData.path)
        ) {
          return {
            ...page,
            ...evData.updatedPageInfo,
          };
        }
        return page;
      });

      const updatedConfig = { ...config, pages: updatedPages };
      handleSendAllConfigs({ type: 'SendAppConfig', updatedConfig });
      dispatch(updateConfig(updatedConfig));
    };

    const handleChangeModule = (
      evData: EditModuleType | EditLayoutModuleType
    ) => {
      const { type, ...updatedModulesConfig } = evData;
      if (!Object.keys(updatedModulesConfig).length) {
        // maybe use it as delete operation??
        return;
      }
      const updatedConfig = {
        ...config,
        modulesConfigs: { ...config.modulesConfigs, ...updatedModulesConfig },
      };

      // DEV =>
      handleSendAllConfigs({ type: 'SendAppConfig', updatedConfig });
      // DEV <=

      dispatch(updateConfig(updatedConfig));
    };

    const handleCreateDupInstance = (evData: CreateDupInstanceType) => {
      const {
        type,
        pageId,
        replaceIndex,
        selectedPage,
        selectedModuleInfo,
        selectedModuleUpdated,
        dupModuleObj,
      } = evData;

      const helperFunc = !selectedModuleInfo?.layoutSection
        ? handleUpdatePageModules
        : handleUpdateLayoutModules;

      const layoutsArr = config.layouts;

      const helperProps = {
        prev: config,
        renderedLayoutArr: selectedModuleInfo?.layoutSection
          ? layoutsArr?.[selectedPage?.layout]?.[
              selectedModuleInfo?.layoutSection
            ]
          : null,
        selectedPage,
        selectedModuleInfo,
        newDupInstance: dupModuleObj,
      };

      const updatedPagesOrLayouts = helperFunc(helperProps);

      const updatedConfig = {
        ...config,
        modulesConfigs: {
          ...config.modulesConfigs,
          [dupModuleObj.instance]: dupModuleObj,
        },
        ...(!selectedModuleInfo?.layoutSection
          ? { pages: updatedPagesOrLayouts }
          : { layouts: updatedPagesOrLayouts }),
      };

      // handleSendAllConfigs({ type: 'SendAppConfig', updatedConfig });
      dispatch(updateConfig(updatedConfig));
    };

    const handleRevertModuleConfig = (evData: RevertModuleConfigType) => {
      const { type, isLayoutUpdate, updatedModulesArr } = evData;

      const updatedConfig = {
        ...config,
        ...(!isLayoutUpdate
          ? { pages: updatedModulesArr }
          : { layouts: updatedModulesArr }),
      };

      // handleSendAllConfigs({ type: 'SendAppConfig', updatedConfig });
      dispatch(updateConfig(updatedConfig));
    };
    const handleSetDynamicPageContent = (evData: SetDynamicPageContent) => {
      const { dynamicPageConfig } = evData;
      dispatch(setPageConfig({ dynamicPageConfig: dynamicPageConfig }));
      evData?.instance && setSelectedInstance(evData?.instance);
    };
    const handleSendNewDup = (evData: SendNewDup) => {
      // debugger;
      console.log('handleSendNewDup evData', evData);
      const {
        module: incomingModuleObj,
        pageId,
        path,
        moduleConfig: incomingModuleConfig,
      } = evData;

      if (!incomingModuleObj || !incomingModuleObj.module) {
        console.warn('handleSendNewDup: invalid module payload', evData);
        return;
      }

      // Update page modules (full replace of the module object where found)
      const updatedPages = config.pages.map((page) => {
        if (page.allow_edit === false) return page;

        if (page.id === pageId || pagesWithSamePath(page.path, path)) {
          const objs = ensureModulesObjects(page?.modules || []);
          const idx = objs.findIndex(
            (m: any) => (m.module ?? m.instance) === incomingModuleObj.module
          );

          if (idx >= 0) {
            const copy = [...objs];
            // FULL REPLACE — incomingModuleObj becomes the module object (this removes old props)
            copy[idx] = { ...incomingModuleObj };
            return { ...page, modules: copy, id: pageId };
          } else {
            // Append if not present
            const appended = [...objs, incomingModuleObj];
            return { ...page, modules: appended, id: pageId };
          }
        }
        return page;
      });

      // Upsert module config into config.modulesConfigs (overwrite if exists)
      let modulesConfigs = { ...(config.modulesConfigs || {}) };
      if (incomingModuleConfig && typeof incomingModuleConfig === 'object') {
        // Determine key to use for modulesConfigs
        const moduleKey =
          incomingModuleConfig.instance ??
          incomingModuleConfig.module ??
          incomingModuleObj.module;

        // Overwrite or add with the incoming config (use the incoming object as authoritative)
        if (moduleKey) {
          modulesConfigs = {
            ...modulesConfigs,
            [moduleKey]: { ...incomingModuleConfig },
          };
        }
      }

      const updatedConfig = {
        ...config,
        pages: updatedPages,
        modulesConfigs,
      };

      handleSendAllConfigs({ type: 'SendAppConfig', updatedConfig });
      return dispatch(updateConfig(updatedConfig));
    };

    const handleRestoreParent = (evData: RestoreParent) => {
      // debugger;
      const { page: incomingPage, pageId, path } = evData;
      if (!incomingPage || !incomingPage.modules) {
        console.warn('handleRestoreParent: invalid payload', evData);
        return;
      }

      const incomingModules = ensureModulesObjects(incomingPage.modules || []);

      const updatedPages = config.pages.map((page) => {
        if (page.allow_edit === false) return page;

        if (page.id === pageId || pagesWithSamePath(page.path, path)) {
          // normalize target page modules
          const objs = ensureModulesObjects(page?.modules || []);

          // build a map for quick lookup of incoming modules by id (module or instance)
          const incomingMap = new Map<string, any>();
          for (const im of incomingModules) {
            const id = (im.module ?? im.instance) as string;
            if (id) incomingMap.set(id, im);
          }

          // replace existing modules that are present in incomingMap
          let replaced = false;
          const replacedArr = objs.map((m: any) => {
            const id = (m.module ?? m.instance) as string;
            if (id && incomingMap.has(id)) {
              replaced = true;
              // FULL replace with incoming module object (keeps incoming's keys)
              return { ...(incomingMap.get(id) || {}) };
            }
            return m;
          });

          // For any incoming modules that were not found in the target page, append them
          const notFoundToAppend = incomingModules
            .filter((im) => {
              const id = (im.module ?? im.instance) as string;
              return !replacedArr.some(
                (rm: any) => (rm.module ?? rm.instance) === id
              );
            })
            .map((im) => ({ ...(im || {}) }));

          const finalModules = [...replacedArr, ...notFoundToAppend];

          // Optionally re-sort finalModules by numeric sort_index ascending so UI ordering follows sort_index:
          finalModules.sort((a: any, b: any) => {
            const na = Number.isFinite(Number(a?.sort_index))
              ? Number(a.sort_index)
              : Number.POSITIVE_INFINITY;
            const nb = Number.isFinite(Number(b?.sort_index))
              ? Number(b.sort_index)
              : Number.POSITIVE_INFINITY;
            return na - nb;
          });

          return { ...page, modules: finalModules, id: pageId };
        }
        return page;
      });

      const updatedConfig = { ...config, pages: updatedPages };

      handleSendAllConfigs({ type: 'SendAppConfig', updatedConfig });
      return dispatch(updateConfig(updatedConfig));
    };

    const handleAddNewLayout = (evData: AddNewLayoutType) => {
      const updatedConfig = {
        ...config,
        layouts: {
          ...config.layouts,
          ...evData.updatedLayouts,
        },
      };
      dispatch(updateConfig(updatedConfig));
    };

    const handleDefaultThemeUpdate = (evData: DefaultThemeType) => {
      const updatedConfig = {
        ...config,
        theme: evData.theme,
      };
      dispatch(updateConfig(updatedConfig));
    };

    const handleSendAllConfigs = (evData: SendAppConfig) => {
      window.parent.postMessage(
        {
          type: 'GetAllConfigs',
          message: { config: evData?.updatedConfig ?? config },
        },
        '*'
      );
    };

    const eventHandler = (event: MessageEvent<any>) => {
      // if (!isValidEventType(event.data.type)) {
      if (!event.data.type) {
        return;
      }
      const evData: SiteEngineConfigEvents = event.data;
      console.log('evData', evData);
      switch (evData.type) {
        // case 'SaveWhiteLabelConfig':
        //   handleSaveWhiteLabelConfigs(evData);
        //   break;
        case 'SendAppConfig':
          handleSendAllConfigs(evData);
          break;
        // case 'SendUpdatedAppConfig':
        //   handleSendUpdatedConfigs(evData);
        //   break;
        case 'RemoveEditOverlay':
          handleRemoveEditOverlay(evData);
          break;
        case 'AddNewModule':
          handleAddNewModule(evData);
          break;
        case 'AddNewLayoutModule':
          handleAddNewLayoutModule(evData);
          break;
        case 'AddNewLayout':
          handleAddNewLayout(evData);
          break;
        case 'ChangeLayout':
          handleChangeLayout(evData);
          break;
        case 'ChangePageInfo':
          handleChangePageInfo(evData);
          break;
        case 'DefaultTheme':
          handleDefaultThemeUpdate(evData);
          break;
        case 'EditLayout':
        case 'EditModule':
          handleChangeModule(evData);
          break;
        case 'CreateDupInstance':
          handleCreateDupInstance(evData);
          break;
        case 'RevertModuleConfig':
          handleRevertModuleConfig(evData);
          break;
        case "SetDynamicPageContent":
          handleSetDynamicPageContent(evData);
          break;
        case "SendNewDup":
          handleSendNewDup(evData);
          break;
        case "RestoreParent":
          handleRestoreParent(evData);
          break;
        // case 'EditLayout':
        //   handleChangeLayoutModule(evData);
        //   break;
        default:
        // console.log('NO SUCH EVENT');
      }
    };
    window.addEventListener('message', eventHandler);
    return () => window.removeEventListener('message', eventHandler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config]);

  return {
    selectedInstance,
    setSelectedInstance,
  };
};

export default useHandleMessagesFromBO;
